import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Tabs from '@radix-ui/react-tabs';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  FileText,
  Loader2,
  Pencil,
  Save,
  Send,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { cn } from '../lib/cn';
import { useNotifications } from '../store/useStore';
import { BlockEditor } from '../components/email/BlockEditor';
import { SalvaModelloDialog } from '../components/email/modelli/SalvaModelloDialog';
import { SceltaModelloDialog } from '../components/email/modelli/SceltaModelloDialog';
import { EmailPreview } from '../components/email/EmailPreview';
import { SceltaDestinatari } from '../components/email/liste/SceltaDestinatari';
import { campaignService, emailService } from '../lib/email/emailService';
import { listeService } from '../lib/email/listeService';
import { descriviPubblico, problemaPubblico } from '../lib/email/pubblico';
import {
  EMAIL_CAMPAIGN_STATUS,
  type EmailCampaign,
  type EmailCampaignPreparata,
  type EmailCampaignPubblico,
  type EmailLista,
} from '../types/email.types';
import { documentoVuoto } from '@email/index.ts';
import type { DocumentoEmail } from '@email/types.ts';

/** Stati in cui la campagna non si tocca piu'. */
const BLOCCATI = ['in_invio', 'inviata', 'annullata'];

export default function EmailCampaignEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  const [campagna, setCampagna] = useState<EmailCampaign | null>(null);
  const [documento, setDocumento] = useState<DocumentoEmail>(documentoVuoto());
  const [nome, setNome] = useState('');
  const [oggetto, setOggetto] = useState('');
  const [preheader, setPreheader] = useState('');
  const [pubblico, setPubblico] = useState<EmailCampaignPubblico>('da_scegliere');
  const [listeScelte, setListeScelte] = useState<string[]>([]);
  const [liste, setListe] = useState<EmailLista[]>([]);
  const [totaleAttivi, setTotaleAttivi] = useState(0);

  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [destinatari, setDestinatari] = useState<EmailCampaignPreparata | null>(null);
  const [calcolando, setCalcolando] = useState(false);
  const [quota, setQuota] = useState(0);
  const [modalita, setModalita] = useState<'sandbox' | 'reale'>('sandbox');
  const [confermaAperta, setConfermaAperta] = useState(false);
  const [salvaModelloAperta, setSalvaModelloAperta] = useState(false);
  const [sceltaModelloAperta, setSceltaModelloAperta] = useState(false);
  const [inviando, setInviando] = useState(false);

  const primoCaricamento = useRef(true);
  // Ogni cambio di destinatari fa partire un calcolo: se nel frattempo se ne
  // e' fatto un altro, il risultato vecchio non deve sovrascrivere il nuovo.
  const ultimoCalcolo = useRef(0);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [c, q, m, elencoListe, riepilogo] = await Promise.all([
          campaignService.dettaglio(id),
          campaignService.quotaResidua(),
          campaignService.modalita(),
          listeService.elenco(),
          emailService.getStats(),
        ]);
        setCampagna(c);
        setNome(c.name);
        setOggetto(c.subject);
        setPreheader(c.preheader ?? '');
        setPubblico(c.pubblico ?? 'da_scegliere');
        setListeScelte(c.tag_filter ?? []);
        setListe(elencoListe);
        setTotaleAttivi(riepilogo.attivi);
        setDocumento((c.document as DocumentoEmail) ?? documentoVuoto());
        setQuota(q);
        setModalita(m);
        if (c.target_count > 0) {
          setDestinatari({ destinatari: c.target_count, esclusi_per_consenso: 0 });
        }
      } catch (errore) {
        addNotification({
          type: 'error',
          title: 'Campagna non trovata',
          message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
        });
        navigate('/mailing/campagne');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, addNotification]);

  const modificabile = campagna ? !BLOCCATI.includes(campagna.status) : false;

  // Salvataggio automatico: si scrive e basta, senza dover premere niente.
  // La pausa evita una scrittura per ogni tasto premuto.
  useEffect(() => {
    if (!id || loading || !modificabile) return;
    if (primoCaricamento.current) {
      primoCaricamento.current = false;
      return;
    }

    const attesa = setTimeout(async () => {
      setSalvando(true);
      try {
        await campaignService.salva(id, {
          name: nome,
          subject: oggetto,
          preheader: preheader || null,
          document: documento,
        });
      } catch (errore) {
        addNotification({
          type: 'error',
          title: 'Salvataggio non riuscito',
          message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
        });
      } finally {
        setSalvando(false);
      }
    }, 900);

    return () => clearTimeout(attesa);
  }, [id, nome, oggetto, preheader, documento, loading, modificabile, addNotification]);

  const calcolaDestinatari = useCallback(async () => {
    if (!id) return;
    const questo = ++ultimoCalcolo.current;
    setCalcolando(true);
    try {
      const risultato = await campaignService.preparaDestinatari(id);
      if (questo === ultimoCalcolo.current) setDestinatari(risultato);
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Non riesco a calcolare i destinatari',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    } finally {
      if (questo === ultimoCalcolo.current) setCalcolando(false);
    }
  }, [id, addNotification]);

  /**
   * Cambia a chi va la campagna. Si salva subito, e il database butta via i
   * destinatari calcolati prima; poi, se la scelta e' completa, si ricalcola.
   */
  const cambiaPubblico = useCallback(
    async (nuovo: EmailCampaignPubblico, nuoveListe: string[]) => {
      if (!id) return;
      setPubblico(nuovo);
      setListeScelte(nuoveListe);
      setDestinatari(null);
      try {
        await campaignService.salvaPubblico(id, nuovo, nuoveListe);
        if (!problemaPubblico({ pubblico: nuovo, liste: nuoveListe })) await calcolaDestinatari();
      } catch (errore) {
        addNotification({
          type: 'error',
          title: 'Non riesco a salvare i destinatari',
          message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
        });
      }
    },
    [id, calcolaDestinatari, addNotification]
  );

  /** Cosa manca prima di poter spedire. */
  const problemi = useMemo(() => {
    const trovati: string[] = [];
    const sulPubblico = problemaPubblico({ pubblico, liste: listeScelte });
    if (sulPubblico) trovati.push(sulPubblico);
    if (!oggetto.trim()) trovati.push('Manca l\'oggetto');
    if (documento.blocchi.length === 0) trovati.push('La email e\' vuota');
    if (!sulPubblico && !destinatari) trovati.push('I destinatari non sono ancora stati calcolati');
    else if (destinatari && destinatari.destinatari === 0) trovati.push('Nessun destinatario puo\' ricevere questa campagna');
    return trovati;
  }, [pubblico, listeScelte, oggetto, documento, destinatari]);

  const giorniStimati = useMemo(() => {
    if (!destinatari || quota <= 0) return null;
    return Math.ceil(destinatari.destinatari / Math.max(quota, 1));
  }, [destinatari, quota]);

  const nomiListe = useMemo(() => Object.fromEntries(liste.map((l) => [l.id, l.name])), [liste]);

  const invia = useCallback(async () => {
    if (!id) return;
    setInviando(true);
    try {
      await campaignService.salva(id, {
        name: nome,
        subject: oggetto,
        preheader: preheader || null,
        document: documento,
      });
      // Si ricalcola proprio adesso: fra il calcolo mostrato e il clic
      // qualcuno puo' essersi disiscritto o essere entrato in una lista.
      const fresco = await campaignService.preparaDestinatari(id);
      if (fresco.destinatari === 0) {
        throw new Error('Nessun destinatario puo\' ricevere questa campagna.');
      }
      await campaignService.programma(id, new Date());
      addNotification({
        type: 'success',
        title: modalita === 'reale' ? 'Campagna avviata' : 'Campagna avviata in prova',
        message:
          modalita === 'reale'
            ? 'Il motore la prende in carico entro 5 minuti.'
            : 'Siamo in modalita\' di prova: i messaggi si comporranno ma non usciranno.',
      });
      setConfermaAperta(false);
      navigate('/mailing/campagne');
    } catch (errore) {
      addNotification({
        type: 'error',
        title: 'Avvio non riuscito',
        message: errore instanceof Error ? errore.message : 'Errore imprevisto.',
      });
    } finally {
      setInviando(false);
    }
  }, [id, nome, oggetto, preheader, documento, modalita, navigate, addNotification]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-9 w-64 rounded-lg bg-gray-100 animate-pulse" />
        <Card className="h-32 animate-pulse bg-gray-50 border-gray-100" />
        <Card className="h-96 animate-pulse bg-gray-50 border-gray-100" />
      </div>
    );
  }

  if (!campagna) return null;

  const stato = EMAIL_CAMPAIGN_STATUS[campagna.status];

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => navigate('/mailing/campagne')}
            className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-800 mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Tutte le campagne
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {modificabile ? (
              <Input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="text-xl md:text-2xl font-bold border-0 px-0 h-auto focus-visible:ring-0 bg-transparent"
              />
            ) : (
              <h1 className="text-xl md:text-2xl font-bold text-slate-900">{nome}</h1>
            )}
            <span
              className={cn(
                'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border flex-shrink-0',
                stato.badge
              )}
              title={stato.spiegazione}
            >
              {stato.label}
            </span>
            {salvando && (
              <span className="inline-flex items-center gap-1 text-[11px] text-neutral-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                salvo…
              </span>
            )}
          </div>
        </div>

        {modificabile && (
          <Button
            onClick={() => setConfermaAperta(true)}
            disabled={problemi.length > 0}
            className="h-10 px-4 shadow-lg shadow-primary-500/20 bg-primary-600 hover:bg-primary-700 text-white flex-shrink-0"
          >
            <Send className="w-4 h-4 mr-2" />
            Invia
          </Button>
        )}
      </div>

      {!modificabile && (
        <div className="flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <CheckCircle2 className="w-5 h-5 text-neutral-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-neutral-600 leading-relaxed">
            {stato.spiegazione} Una campagna partita non si modifica piu': per cambiarne il
            contenuto se ne crea una nuova.
          </p>
        </div>
      )}

      {/* Oggetto e riga di anteprima */}
      <Card className="border-gray-200">
        <CardContent className="p-4 space-y-3">
          <div>
            <Label htmlFor="oggetto" className="text-xs">
              Oggetto <span className="text-primary-600">*</span>
            </Label>
            <Input
              id="oggetto"
              value={oggetto}
              onChange={(e) => setOggetto(e.target.value)}
              disabled={!modificabile}
              placeholder="Quello che si legge nella posta in arrivo"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="preheader" className="text-xs">
              Riga di anteprima
            </Label>
            <Input
              id="preheader"
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              disabled={!modificabile}
              placeholder="La frase che compare accanto all'oggetto, prima di aprire"
              className="mt-1"
            />
            <p className="text-[11px] text-neutral-400 mt-1">
              Se la lasci vuota, i programmi di posta ci mettono le prime parole della email, che
              di solito non dicono niente.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Destinatari */}
      <SceltaDestinatari
        pubblico={pubblico}
        listeScelte={listeScelte}
        liste={liste}
        totaleAttivi={totaleAttivi}
        destinatari={destinatari}
        calcolando={calcolando}
        modificabile={modificabile}
        quota={quota}
        onCambia={cambiaPubblico}
        onRicalcola={calcolaDestinatari}
      />

      {/* Contenuto */}
      <Tabs.Root defaultValue="componi">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <Tabs.List className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          <Tabs.Trigger
            value="componi"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-neutral-500 data-[state=active]:bg-primary-600 data-[state=active]:text-white transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            Componi
          </Tabs.Trigger>
          <Tabs.Trigger
            value="anteprima"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-neutral-500 data-[state=active]:bg-primary-600 data-[state=active]:text-white transition-colors"
          >
            <Eye className="w-3.5 h-3.5" />
            Anteprima
          </Tabs.Trigger>
        </Tabs.List>

          {modificabile && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setSceltaModelloAperta(true)}>
                <FileText className="w-4 h-4 mr-2" />
                Parti da un modello
              </Button>
              <Button
                variant="outline"
                onClick={() => setSalvaModelloAperta(true)}
                disabled={documento.blocchi.length === 0}
              >
                <Save className="w-4 h-4 mr-2" />
                Salva come modello
              </Button>
            </div>
          )}
        </div>

        <Tabs.Content value="componi">
          {modificabile ? (
            <BlockEditor documento={documento} onChange={setDocumento} />
          ) : (
            <EmailPreview documento={documento} oggetto={oggetto} preheader={preheader} />
          )}
        </Tabs.Content>

        <Tabs.Content value="anteprima">
          <EmailPreview documento={documento} oggetto={oggetto} preheader={preheader} />
        </Tabs.Content>
      </Tabs.Root>

      <SalvaModelloDialog
        aperto={salvaModelloAperta}
        onChiudi={setSalvaModelloAperta}
        documento={documento}
        oggetto={oggetto}
        preheader={preheader}
        nomeProposto={nome}
        ambitoProposto="campagne"
      />

      <SceltaModelloDialog
        aperto={sceltaModelloAperta}
        onChiudi={setSceltaModelloAperta}
        ambito="campagne"
        sovrascrive={documento.blocchi.length > 0}
        onScelto={(modello) => {
          setDocumento(modello.document);
          // L'oggetto del modello non sovrascrive quello gia' scritto: se
          // qualcuno lo ha pensato per questa campagna, vale piu' del modello.
          if (!oggetto.trim()) setOggetto(modello.subject);
          if (!preheader.trim() && modello.preheader) setPreheader(modello.preheader);
        }}
      />

      {/* Conferma */}
      <Dialog open={confermaAperta} onOpenChange={(v) => !v && setConfermaAperta(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {modalita === 'reale' ? 'Invio' : 'Prova di invio'}
            </DialogTitle>
            <DialogDescription>
              {modalita === 'reale'
                ? 'Una volta partita non si puo\' richiamare indietro.'
                : 'Siamo in modalita\' di prova: i messaggi si comporranno ma non usciranno.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
              <p className="font-semibold text-gray-900">{destinatari?.destinatari ?? 0} destinatari</p>
              <p className="text-xs text-neutral-600 mt-1 break-words">
                {descriviPubblico({ pubblico, liste: listeScelte }, nomiListe)}
              </p>
              <p className="text-xs text-neutral-500 mt-1 break-words">Oggetto: {oggetto}</p>
              {giorniStimati && giorniStimati > 1 && (
                <p className="text-xs text-amber-700 mt-2">
                  L'invio sara' spalmato su circa {giorniStimati} giorni per rispettare il tetto
                  giornaliero.
                </p>
              )}
            </div>

            {problemi.length > 0 && (
              <div className="flex gap-2.5 rounded-lg border border-red-200 bg-red-50 p-2.5">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <ul className="text-xs text-red-900 space-y-0.5">
                  {problemi.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-[11px] text-neutral-400 leading-relaxed">
              In fondo a ogni messaggio ci sono il link di disiscrizione e i dati dell'azienda:
              sono obbligatori e vengono aggiunti da soli.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfermaAperta(false)} disabled={inviando}>
              Annulla
            </Button>
            <Button
              onClick={invia}
              disabled={problemi.length > 0 || inviando}
              className="bg-primary-600 hover:bg-primary-700 text-white"
            >
              {inviando ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {modalita === 'reale' ? 'Invia adesso' : 'Avvia la prova'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
